import {
  type BusinessTaskKind,
  getAdminTaskTrace,
  type TaskTraceEvent,
} from "@/services/drama/api";
import { Alert, Button, Drawer, Empty, Spin, Switch, Tag, Typography } from 'antd';
import { createStyles } from "antd-style";
import dayjs from "dayjs";
import React from "react";
import { TouchSafeTooltip as Tooltip } from '@/shared/components/TouchSafeTooltip';

const PHASE_COLOR: Record<string, string> = {
  submit: "blue",
  poll: "cyan",
  download: "default",
  sse: "geekblue",
  llm_submit: "purple",
  llm_response: "magenta",
  enqueue: "default",
  retry_scheduled: "orange",
  terminal: "green",
  webhook: "gold",
  provider_failover: "volcano",
};

// phase → 醒目中文标签;缺省回落原始 phase 字符串。
const PHASE_LABEL: Record<string, string> = {
  submit: "提交",
  poll: "轮询",
  download: "下载",
  sse: "SSE",
  llm_submit: "LLM 提交",
  llm_response: "LLM 响应",
  enqueue: "入队",
  retry_scheduled: "重试",
  terminal: "终态",
  webhook: "回调",
  provider_failover: "🔀 供应商转移",
};

const useStyles = createStyles(({ token }) => ({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  meta: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    fontSize: 12,
    color: token.colorTextSecondary,
    marginBottom: 8,
  },
  row: {
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadiusSM,
    marginBottom: 8,
    background: token.colorBgContainer,
  },
  rowHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: 12,
    flexWrap: "wrap",
  },
  rowBody: {
    borderTop: `1px solid ${token.colorBorderSecondary}`,
    padding: 10,
    fontSize: 11,
    fontFamily: token.fontFamilyCode,
    background: token.colorFillTertiary,
  },
  section: {
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 500,
    color: token.colorTextSecondary,
    marginBottom: 4,
  },
  preWrap: {
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-all" as const,
    maxHeight: 320,
    overflowY: "auto" as const,
    background: token.colorBgContainer,
    padding: 8,
    borderRadius: token.borderRadiusSM,
    border: `1px solid ${token.colorBorderSecondary}`,
  },
  urlCell: {
    fontFamily: token.fontFamilyCode,
    fontSize: 11,
    color: token.colorTextSecondary,
    flex: 1,
    minWidth: 200,
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
    whiteSpace: "nowrap" as const,
  },
}));

export interface TraceDrawerProps {
  open: boolean;
  taskId: string; // BusinessTask.id "{kind}:{target_id}"
  kind: BusinessTaskKind;
  targetId: string;
  onClose: () => void;
}

const TraceDrawer: React.FC<TraceDrawerProps> = ({
  open,
  taskId,
  kind,
  targetId,
  onClose,
}) => {
  const { styles } = useStyles();
  const [loading, setLoading] = React.useState(false);
  const [events, setEvents] = React.useState<TaskTraceEvent[]>([]);
  const [onlyError, setOnlyError] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const fetch = React.useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminTaskTrace(kind, targetId, {
        onlyError,
        limit: 500,
      });
      if (res?.success && res.data) {
        setEvents(res.data.events || []);
      } else {
        setError(res?.error?.code || "load failed");
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "request failed");
    } finally {
      setLoading(false);
    }
  }, [open, kind, targetId, onlyError]);

  React.useEffect(() => {
    fetch();
  }, [fetch]);

  React.useEffect(() => {
    if (!open) {
      setExpanded(new Set());
      setOnlyError(false);
    }
  }, [open]);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const summary = React.useMemo(() => {
    if (!events.length) return null;
    const first = events[0]?.created_at;
    const last = events[events.length - 1]?.created_at;
    const errors = events.filter((e) => e.error).length;
    const providers = Array.from(
      new Set(events.map((e) => e.provider).filter(Boolean))
    );
    const models = Array.from(
      new Set(events.map((e) => e.model).filter(Boolean))
    );
    return { first, last, total: events.length, errors, providers, models };
  }, [events]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <span style={{ fontFamily: "monospace", fontSize: 13 }}>
          调用链 · {taskId}
        </span>
      }
      width={760}
      placement="right"
      destroyOnHidden
    >
      <div className={styles.toolbar}>
        <Switch
          size="small"
          checked={onlyError}
          onChange={setOnlyError}
          checkedChildren="只看错误"
          unCheckedChildren="全部"
        />
        <Button size="small" loading={loading} onClick={fetch}>
          刷新
        </Button>
      </div>

      {summary && (
        <div className={styles.meta}>
          <span>事件 {summary.total}</span>
          {summary.errors > 0 && (
            <span style={{ color: "var(--ant-color-error-text)" }}>
              错误 {summary.errors}
            </span>
          )}
          <span>起 {dayjs(summary.first).format("HH:mm:ss")}</span>
          <span>止 {dayjs(summary.last).format("HH:mm:ss")}</span>
          {summary.providers.length > 0 && (
            <span>供应商 {summary.providers.join(" → ")}</span>
          )}
          {summary.models.length > 0 && (
            <span>模型 {summary.models.join(" / ")}</span>
          )}
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      {loading && !events.length ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : events.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无 trace 事件"
        />
      ) : (
        events.map((evt) => {
          const isExp = expanded.has(evt.id);
          const phaseColor = PHASE_COLOR[evt.phase] || "default";
          const phaseLabel = PHASE_LABEL[evt.phase] || evt.phase;
          const isFailover = evt.phase === "provider_failover";
          const statusBad =
            evt.error || (evt.status_code && evt.status_code >= 400);
          return (
            <div key={evt.id} className={styles.row}>
              <div className={styles.rowHeader} onClick={() => toggle(evt.id)}>
                <Tag
                  color={phaseColor}
                  style={{
                    marginInlineEnd: 0,
                    fontWeight: isFailover ? 600 : undefined,
                  }}
                >
                  {phaseLabel}
                </Tag>
                <span style={{ color: "var(--ant-color-text-tertiary)" }}>
                  {dayjs(evt.created_at).format("HH:mm:ss.SSS")}
                </span>
                {evt.attempt && evt.attempt > 0 ? (
                  <Tag>#{evt.attempt}</Tag>
                ) : null}
                {evt.method && (
                  <span style={{ fontFamily: "monospace", fontWeight: 500 }}>
                    {evt.method}
                  </span>
                )}
                {evt.provider && (
                  <Tag style={{ marginInlineEnd: 0 }}>
                    供应商:{evt.provider}
                  </Tag>
                )}
                {evt.model && (
                  <Tag style={{ marginInlineEnd: 0 }}>模型:{evt.model}</Tag>
                )}
                <span className={styles.urlCell}>
                  {evt.url || evt.note || "-"}
                </span>
                {evt.status_code ? (
                  <Tag color={statusBad ? "error" : "success"}>
                    {evt.status_code}
                  </Tag>
                ) : null}
                {evt.latency_ms ? (
                  <span style={{ color: "var(--ant-color-text-tertiary)" }}>
                    {evt.latency_ms}ms
                  </span>
                ) : null}
                {evt.error && (
                  <Tooltip title={evt.error}>
                    <Tag color="error">错误</Tag>
                  </Tooltip>
                )}
              </div>
              {isExp && (
                <div className={styles.rowBody}>
                  {evt.error && (
                    <div className={styles.section}>
                      <div className={styles.sectionTitle}>error</div>
                      <div className={styles.preWrap}>{evt.error}</div>
                    </div>
                  )}
                  {evt.note && (
                    <div className={styles.section}>
                      <div className={styles.sectionTitle}>note</div>
                      <div className={styles.preWrap}>{evt.note}</div>
                    </div>
                  )}
                  {(evt.provider || evt.model) && (
                    <div className={styles.section}>
                      <div className={styles.sectionTitle}>
                        provider / model
                      </div>
                      <Typography.Text style={{ fontSize: 11 }}>
                        {evt.provider}
                        {evt.model ? ` · ${evt.model}` : ""}
                      </Typography.Text>
                    </div>
                  )}
                  {evt.request_headers &&
                    Object.keys(evt.request_headers).length > 0 && (
                      <div className={styles.section}>
                        <div className={styles.sectionTitle}>
                          request headers
                        </div>
                        <div className={styles.preWrap}>
                          {Object.entries(evt.request_headers)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join("\n")}
                        </div>
                      </div>
                    )}
                  {evt.request_body && (
                    <div className={styles.section}>
                      <div className={styles.sectionTitle}>request body</div>
                      <div className={styles.preWrap}>{evt.request_body}</div>
                    </div>
                  )}
                  {evt.response_headers &&
                    Object.keys(evt.response_headers).length > 0 && (
                      <div className={styles.section}>
                        <div className={styles.sectionTitle}>
                          response headers
                        </div>
                        <div className={styles.preWrap}>
                          {Object.entries(evt.response_headers)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join("\n")}
                        </div>
                      </div>
                    )}
                  {evt.response_body && (
                    <div className={styles.section}>
                      <div className={styles.sectionTitle}>response body</div>
                      <div className={styles.preWrap}>{evt.response_body}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </Drawer>
  );
};

export default TraceDrawer;
